<template>
  <b-container class="chat-box">
    <b-row>
      <b-col cols="12" lg="10" class="mx-auto">
        <div
          v-for="(msg, msgidx) in messages"
          :key="msgidx"
          class="chat-record mt-2"
        >
          <ChatMessageComponent
            :style="{
              '--main-color': mainColor,
              '--secondary-color': secondaryColor,
            }"
            :initMsg="msg"
            :key="msgidx"
            :msgidx="msgidx"
            :ref="`msgRef-${msg.name}`"
            :context="context"
            :condition="condition"
            @select-card="handleCardClick"
            @continue="continueChat"
            @scroll-to-bottom="scrollToBottom"
            @scroll-to-msg="scrollToMsg"
            @go-to-step="handleGoToStep"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
// Logged-out demo for the Eureka product. Reached when a visitor clicks the
// Eureka teaser (see Default.changeView). It reuses the exact same machinery as
// the logged-in chatbot (ChatMessageComponent + a `questions` graph) so it looks
// and feels identical.
//
// Lo que cuenta es lo mismo que cuenta /eureka/, y lo cuenta con el
// MISMO texto: el contenido baja de `informationService.getServiceContent`, que
// sirve el contexto de esa pagina desde el registro de marketing. Antes estaba
// copiado aca a mano, y el precio y las preguntas frecuentes quedaban a merced
// de que alguien se acordara de tocar los dos lados.
//
// El recorrido es: bienvenida con la informacion del servicio -> menu, y desde
// el menu preguntas frecuentes, precios, cotizacion o iniciar sesion para usar
// la version real (emite `open-login`, lo atiende el layout).
import ChatMessageComponent from "./ChatMessageComponent.vue";
import { fetchRewards, applyBonus, loginLabel } from "./signupRewards";
import informationService from "@/services/informationService";
import {
  sectionStep,
  sectionComponent as seccion,
} from "./serviceSections";

// El ambar de Eureka y su version oscura para texto (`accent_key='eureka'` en
// pages.py). Es el color del producto, no el `primary` de la marca.
const ACENTO = "#f8ac04";
const ACENTO_INK = "#c28e02";

const sectionComponent = (content, block) =>
  seccion(content, block, ACENTO, ACENTO_INK);

export default {
  name: "EurekaDemo",
  components: { ChatMessageComponent },
  // Absorb the props the layout's router-view binds so they don't leak onto the
  // root element; none of them are needed by the demo.
  props: {
    bannerLoaded: { type: Boolean, required: false, default: false },
    routeName: { type: String, required: false, default: "" },
    tags: { type: Array, required: false, default: () => [] },
  },
  data() {
    return {
      mainColor: "#efba50",
      secondaryColor: "#d9bd70",
      currentQuestionIndex: "",
      condition: "",
      context: {},
      messages: [],
      questions: {
        welcomeMessage: sectionStep(
          "welcomeMessage",
          "👋 ¡Hola! Eureka es una prueba estandarizada en Chile para el diagnóstico de DEA, especialmente creada para que los establecimientos educativos puedan evaluar en el contexto del Programa de Integración Escolar y realizar el ingreso a PIE.",
          // El menú aparece solo debajo de la información: no hay nada que
          // responder acá. El botón de Google va bajo el saludo, para entrar
          // sin bajar hasta el menú.
          { dontShowNextButton: true, loginButton: true }
        ),
        mainMessage: {
          name: "mainMessage",
          isMain: true,
          text: "<strong>¿Qué te gustaría hacer?</strong>",
          type: "options",
          answersLoaded: false,
          single_choice: true,
          answers: [],
          process_dic: [
            {
              name: "Iniciar sesión para probar la versión real",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            {
              name: "Ver las preguntas frecuentes",
              nextQuestionIndex: "faq",
            },
            {
              name: "Ver los precios y planes",
              nextQuestionIndex: "prices",
            },
            {
              name: "Recibir una cotización",
              nextQuestionIndex: "bill",
              // La cotizacion no se agota: quien ya pidio una puede querer otra
              // por otra cantidad de aplicaciones.
              always: true,
            },
          ],
          // Rebuilt on every visit: a step already taken has its name as a key
          // in `context`, so its button is dropped instead of coming back —
          // and the fresh objects keep a past `isSelected` from sticking.
          getAnswers(context) {
            this.answers = this.process_dic
              .filter(
                (ans) =>
                  ans.always ||
                  !ans.nextQuestionIndex ||
                  !(ans.nextQuestionIndex in context)
              )
              .map((ans) => ({
                ...ans,
                isSelected: false,
                class: "col-md-7 col-12 my-2",
                bootstrap_classes: "text-left px-2 font-weight-bold",
              }));
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        faq: sectionStep(
          "faq",
          "Estas son las preguntas que más nos hacen sobre Eureka y el Decreto 170:",
          { dontShowNextButton: true }
        ),
        prices: sectionStep(
          "prices",
          "Eureka se puede usar de tres formas, según lo que necesites:",
          { dontShowNextButton: true }
        ),
        // Same cotización endpoint as the logged-in chatbot. Anonymous callers
        // get a Turnstile token attached by the api interceptor
        // (`send_eureka_bill/` is in CAPTCHA_ENDPOINTS) and the backend gates
        // the action with CaptchaRequired + MailAnonThrottle.
        bill: {
          name: "bill",
          text: "¡Excelente! Indícanos tu correo y la cantidad de aplicaciones a cotizar y te enviaremos la cotización.",
          type: "inputs",
          answersLoaded: true,
          answers: [
            {
              name: "email",
              type: "email",
              placeholder: "Escribe tu correo aquí",
              class: "col-12 col-md-6 mb-2",
            },
            {
              name: "applications",
              type: "number",
              placeholder: "Cantidad de aplicaciones",
              class: "col-12 col-md-6 mb-2",
            },
          ],
          nextMessage: "Obtener cotización!",
          extraFunction: (data) => {
            informationService
              .sendEurekaBill({
                email: data.email,
                applications: data.applications,
                service: "EUR_STU",
              })
              .then((resp) => {
                this.$toasted.success(resp.message);
              })
              .catch(() => {
                this.$toasted.error(
                  "No pudimos enviar tu cotización. Por favor, inténtalo nuevamente."
                );
              });
          },
          nextQuestionIndex: "mainMessage",
          routine: () => {},
        },
        loginMessage: {
          name: "loginMessage",
          text: "¿Te gustaría evaluar a tus propios estudiantes y descargar el informe psicopedagógico? Inicia sesión y prueba la versión real.",
          type: "options",
          answersLoaded: false,
          single_choice: true,
          answers: [],
          process_dic: [
            {
              name: "Iniciar sesión para probar la versión real",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            {
              name: "Conocer más detalles de la prueba",
              option: "detalles",
            },
            {
              name: "Recibir una cotización",
              nextQuestionIndex: "bill",
            },
          ],
          // Rebuilt on every visit: a step already taken has its name as a key
          // in `context`, so its button is dropped instead of coming back —
          // and the fresh objects keep a past `isSelected` from sticking.
          getAnswers(context) {
            this.answers = this.process_dic
              .filter(
                (ans) =>
                  !ans.nextQuestionIndex || !(ans.nextQuestionIndex in context)
              )
              .map((ans) => ({
                ...ans,
                isSelected: false,
                class: "col-md-7 col-12 my-2",
                bootstrap_classes: "text-left px-2 font-weight-bold",
              }));
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
      },
    };
  },
  created() {
    this.$store.commit("chatbot/clear");
    // El primer mensaje espera a las dos: los usos gratis se prometen con el
    // número real de la config, y la información del servicio es la de la
    // página de producto. Si alguna falla, esa mitad simplemente no se cuenta.
    Promise.all([
      fetchRewards("Eureka"),
      informationService.getServiceContent("eureka").catch(() => null),
    ]).then(([rewards, content]) => {
      this.applyContent(content);
      // La promesa de usos gratis va en el botón de Google del saludo y en el
      // menú; repetirla también en el texto del saludo la decía dos veces
      // seguidas.
      applyBonus(this.questions, rewards, ["mainMessage"]);
      this.questions.welcomeMessage.loginLabel = loginLabel(rewards, "Iniciar sesión para probar la versión real");
      this.currentQuestionIndex = "welcomeMessage";
      const question = this.questions[this.currentQuestionIndex];
      this.context[question.name] = {};
      this.messages.push(question);
    });
  },
  computed: {
    token() {
      return this.$store.state.login.token;
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
  },
  watch: {
    // Once the visitor logs in (from a CTA), send them to the real product.
    token(val) {
      if (val) this.goToRealProduct();
    },
  },
  mounted() {
    // A logged-in user has no business on the demo (e.g. landed here after a
    // login that reloaded the page) — forward them to the real chatbot.
    if (this.token) this.goToRealProduct();
  },
  methods: {
    // Reparte el contenido de /eureka/ por los pasos que lo muestran.
    // Sin contenido —el endpoint no respondió— el FAQ y los precios no tienen
    // de dónde salir, y se retiran del menú: mejor no ofrecerlos que abrir una
    // tarjeta vacía. La bienvenida y la cotización siguen en pie.
    applyContent(content) {
      if (content) {
        if (content.hero_lede) {
          this.questions.welcomeMessage.text += `<br/>${content.hero_lede}`;
        }
        this.questions.welcomeMessage.component = sectionComponent(
          content,
          "info"
        );
        this.questions.faq.component = sectionComponent(content, "faq");
        this.questions.prices.component = sectionComponent(content, "planes");
      }
      // Sin contenido esos dos pasos no tienen nada que dibujar, así que se
      // retiran del menú: mejor no ofrecerlos que abrir una sección vacía. La
      // bienvenida se queda con su saludo y la cotización sigue en pie.
      const menu = this.questions.mainMessage;
      menu.process_dic = menu.process_dic.filter(
        (ans) => content || !["faq", "prices"].includes(ans.nextQuestionIndex)
      );
    },
    // El botón del plan gratis pide iniciar sesión, igual que la opción
    // destacada del menú.
    handleGoToStep(step) {
      if (step === "login") this.login();
    },
    handleCardClick(answer, msgidx, ansidx, max) {
      if (this.messages[msgidx].name === this.currentQuestion.name) {
        this.selectCard(msgidx, ansidx, max);
        this.selectAnswer();
      }
    },
    selectCard(msgidx, ansidx) {
      if (this.currentQuestion.single_choice) {
        this.messages[msgidx].answers.forEach((ans) => {
          ans.isSelected = false;
        });
      }
      this.messages[msgidx].answers[ansidx].isSelected =
        !this.messages[msgidx].answers[ansidx].isSelected;
    },
    selectAnswer() {
      if (this.currentQuestion.single_choice) {
        this.continueChat();
      }
    },
    async continueChat() {
      // Resolve the selected answer (for single-choice option steps) so we can
      // both route on it and intercept the "login" action.
      if (
        this.currentQuestion.type === "options" &&
        this.currentQuestion.single_choice
      ) {
        const selected = this.currentQuestion.answers.find(
          (ans) => ans.isSelected
        );
        this.context[this.currentQuestion.name] = selected;
        if (selected && selected.option === "login") {
          this.login();
          return;
        }
        if (selected && selected.option === "detalles") {
          this.verDetalles();
          return;
        }
      }

      this.messages.slice(-1)[0].answered = true;
      if (this.currentQuestion.isFinal) return;

      await this.currentQuestion.routine();
      this.currentQuestionIndex =
        this.context[this.currentQuestion.name] &&
        this.context[this.currentQuestion.name].nextQuestionIndex
          ? this.context[this.currentQuestion.name].nextQuestionIndex
          : this.currentQuestion.nextQuestionIndex;
      this.loadQuestion(this.currentQuestionIndex);
    },
    loadQuestion(questionIndex) {
      try {
        const question = this.questions[questionIndex];
        this.context[question.name] = {};
        this.messages.push(question);
      } catch (error) {
        console.log(error);
      }
    },
    scrollToBottom() {
      const container = document.querySelector(".general-container");
      if (container) window.scrollTo(0, container.scrollHeight);
    },
    scrollToMsg(name) {
      const msgRef = this.$refs[`msgRef-${name}`];
      if (!msgRef || !msgRef.length) return;
      // Un paso que se auto-encadena —un "informative", o una sección sin
      // botón— añade el mensaje siguiente sin que nadie lo pida. Hacerle caso
      // al scroll arrastraría al visitante más allá de lo que acaba de
      // abrirse: mejor quedarse donde está.
      const idx = this.messages.map((m) => m.name).lastIndexOf(name);
      const previo = idx > 0 ? this.messages[idx - 1] : null;
      if (previo && (previo.type === "informative" || previo.dontShowNextButton))
        return;
      const parentMsg = msgRef.at(-1).$el.parentNode;
      // "start", not "center": a message taller than the viewport (the details
      // card list) would otherwise land the user in the middle of it.
      parentMsg.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    },
    login() {
      this.$emit("open-login");
    },
    // La ficha de Eureka dejo de ser un paso del chat: /eureka/ es
    // una pagina de Django que dice lo mismo, la sirve el servidor y ademas
    // es indexable. Se sale del SPA, asi que va por location y no por el
    // router de Vue.
    //
    // `location.search` se conserva a proposito. Ahi viajan el fbclid con el
    // que la Conversions API amarra la visita al anuncio y el ?invita= de los
    // referidos: perderlos aca es perder la atribucion de quien SI se
    // interesa. El fragmento no viaja porque el destino no tiene rutas de Vue.
    verDetalles() {
      window.location.assign("/eureka/" + window.location.search);
    },
    goToRealProduct() {
      if (this.$router.currentRoute.name !== "eureka") {
        this.$router.push("eureka").catch(() => {});
      }
    },
  },
};
</script>
<style scoped>
.chat-box {
  margin: 10px auto;
  width: 100%;
  align-items: space-between;
  justify-content: space-between;
}
</style>
